import { Col, Row, Space } from "antd";
import { useMemo, useState } from "react";
import Icon from '../../../../assets/icons/icon';
import { StageFormNames } from "../../../../contexts/TaxLegalStagesStore";
import _t from "../../../../lang/translate";
import { Vehicle } from "../../../../types/appTypes";
import { CaseUpdatedChanges, TaxLegalChanges } from "../../../../types/entityRecordingTypes";
import { ActiveStageValues, OriginalEvaluation, TaxLegalStage, TaxLegalStageData } from "../../../../types/taxLegalTypes";
import { CasePresentationForm, ComplaintConclusionForm, complaintNames, SubmissionForm } from "../../stages/complaint";
import { ConclusionForm, HearingOfPartiesForm, MaterialsForm, names as priceCheckNames } from "../../stages/priceCheck";
import { RequestForm, names as resumptionNames } from "../../stages/resumption";
import { DetailsButton } from "../components/detailsButton";
import { FieldChangeList, ValueChange } from "../components/fieldChange/fieldChangeList";
import { TimelineItemProps } from "./timelineItems";

export default function Data({ event }: TimelineItemProps<"case_updated">) {
  const [showDetails, setShowDetails] = useState(false)

  return (
    <div>
      <div>
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={'small'}>
              <Icon className="text-dark" size="large" name="sync-outline" />
              <span >{event.displayText}</span>
            </Space>
          </Col>
          <Col>
            <DetailsButton showDetails={showDetails} onClick={() => setShowDetails(!showDetails)} />
          </Col>
        </Row>
      </div>
      {showDetails && (
        <div>
          <TopLevelFieldChanges changes={event.payload?.changes} />
          {event.payload?.changes?.vehicle && (
            <VehicleFieldsChange vehicleChanges={event.payload?.changes.vehicle as CaseUpdatedChanges<Vehicle>} />
          )}
          {event.payload?.changes.activeStageValues && (
            <ActiveStageValuesChange activeStageValues={event.payload.changes.activeStageValues as CaseUpdatedChanges<ActiveStageValues>} />
          )}
          {event.payload?.changes.originalEvaluation && (
            <OriginalEvaluationChanges originalEvaluation={event.payload.changes.originalEvaluation as CaseUpdatedChanges<OriginalEvaluation>} />
          )}
          {event.payload?.changes.stages && (
            <StagesChanges stages={event.payload.changes.stages as CaseUpdatedChanges<TaxLegalStage[]>} />
          )}
        </div>
      )}
    </div>
  )
}

function TopLevelFieldChanges({ changes }: { changes?: TaxLegalChanges }) {
  const mappedChanges = useMemo(() => {
    const mapped: ValueChange = {}
    if (!changes) return {};
    const { caseHandler, nextDeadline, governmentBatchId, penaltyType, penaltyCaseId, status } = changes
    if (status && 'title' in status) {
      mapped['status'] = {
        new: { title: status.title?.new ?? "", id: status.id?.new ?? -1, color: status.color?.new ?? "" },
        old: { title: status.title?.old ?? "", id: status.id?.old ?? -1, color: status.color?.old ?? "" }
      }
    }
    if (penaltyType && 'title' in penaltyType) {
      mapped['penaltyType'] = {
        new: { title: penaltyType.title?.new ?? "", id: penaltyType.id?.new ?? -1, color: penaltyType.color?.new ?? "" },
        old: { title: penaltyType.title?.old ?? "", id: penaltyType.id?.old ?? -1, color: penaltyType.color?.old ?? "" }
      }
    }
    if (caseHandler && 'fullName' in caseHandler) mapped['caseHandler'] = { new: caseHandler.fullName?.new, old: caseHandler.fullName?.old }
    if (nextDeadline) mapped['nextDeadline'] = nextDeadline;
    if (governmentBatchId) mapped['governmentBatchId'] = governmentBatchId;
    if (penaltyCaseId) mapped['penaltyCaseId'] = penaltyCaseId;

    return mapped
  }, [changes])
  return Object.keys(mappedChanges).length ? (
    <div className="mt-05 pl-05 pr-05 pt-03 pb-03 border-light border-radius bg-white">
      <h5>{_t("field", "change")}</h5>
      <FieldChangeList changedFields={mappedChanges} />
    </div>
  ) : null
}

function VehicleFieldsChange({ vehicleChanges }: { vehicleChanges: CaseUpdatedChanges<Vehicle> }) {
  delete vehicleChanges?.remoteData
  return (
    <div className="mt-05 pl-05 pr-05 pt-03 pb-03 border-light border-radius bg-white">
      <h5>{_t("vehicle")}</h5>
      <FieldChangeList changedFields={vehicleChanges as ValueChange} />
    </div>

  )
}

function ActiveStageValuesChange({ activeStageValues }: { activeStageValues: CaseUpdatedChanges<ActiveStageValues> }) {
  return (
    <div className="mt-05 pl-05 pr-05 pt-03 pb-03 border-light border-radius bg-white">
      <h5>Aktive værdier</h5>
      <FieldChangeList changedFields={activeStageValues as ValueChange} />
    </div>

  )
}

function OriginalEvaluationChanges({ originalEvaluation }: { originalEvaluation: CaseUpdatedChanges<OriginalEvaluation> }) {
  return (
    <div className="mt-05 pl-05 pr-05 pt-03 pb-03 border-light border-radius bg-white">
      <h5>Original værdifastsættelse</h5>
      <FieldChangeList changedFields={originalEvaluation as ValueChange} />
    </div>

  )
}

type StageFormId = `${TaxLegalStage['type']}-${StageFormNames}`;

function mapTypeToTitle(type: TaxLegalStage['type']) {
  if (type === 'price_check') return "Pristjek"
  if (type === 'complaint') return "Klagesag"
  if (type === 'resumption') return "Genoptagelse"
  return ""
}
function mapStageFormToTitle(formName: StageFormNames) {
  switch (formName) {
    case "materials": return "Materiale";
    case "hearingOfParties": return "Partshøring";
    case "conclusion": return "Afgørelse";
    case "submission": return "Indsendelse";
    case "request": return "Anmodning";
    case "casePresentation": return "Sagsfremstilling";
    case "complaintConclusion": return "Afgørelse";
    default: return "";
  }
}
function cosntructTitle(stageFormId: StageFormId) {
  const [type, formName] = stageFormId.split("-") as [TaxLegalStage['type'], StageFormNames]
  const typeTitle = mapTypeToTitle(type)
  const formNameTitle = mapStageFormToTitle(formName)
  const title = typeTitle + ' / ' + formNameTitle
  return title
}
type StageFormUpdate = CaseUpdatedChanges<TaxLegalStageData[keyof TaxLegalStageData]>

function handleFormUpdate(stageFormId: StageFormId, data: StageFormUpdate) {
  if (!data) return;

  const baseTitle = cosntructTitle(stageFormId)
  const out: { title: string, data: StageFormUpdate }[] = [];

  switch (stageFormId) {
    case "price_check-materials": {
      const materialsData = data as CaseUpdatedChanges<MaterialsForm>
      const receptionKeys = new Set<keyof MaterialsForm>(['caseStartAt', 'deadlineForDocumentation', 'governmentCaseNumber']);
      const processingKeys = new Set<keyof MaterialsForm>(['submissionDate', 'submittedBy', 'expectedConclusion', 'analysisTags']);
      const reception: Record<string, unknown> = {}
      const processing: Record<string, unknown> = {}

      for (const k in materialsData) {
        const key = k as keyof MaterialsForm
        if (receptionKeys.has(key)) {
          reception[key] = materialsData[key]
        }
        if (processingKeys.has(key)) {
          processing[key] = materialsData[key]
        }
      }
      if (Object.keys(reception).length) {
        out.push({
          title: baseTitle + " / 1. Indsendelse",
          data: reception
        })
      }
      if (Object.keys(reception).length) {
        out.push({
          title: baseTitle + " / 2. Behandling",
          data: processing
        })
      }
      break;
    }
    case "resumption-hearingOfParties":
    case "price_check-hearingOfParties": {
      const hearingOfPartiesForm = data as CaseUpdatedChanges<HearingOfPartiesForm>;
      const receptionKeys = new Set<keyof HearingOfPartiesForm>(priceCheckNames.hearingOfParties.reception);
      const processingKeys = new Set<keyof HearingOfPartiesForm>(priceCheckNames.hearingOfParties.processing);
      const reception: Record<string, unknown> = {}
      const processing: Record<string, unknown> = {}

      for (const k in hearingOfPartiesForm) {
        const key = k as keyof HearingOfPartiesForm
        if (receptionKeys.has(key)) {
          reception[key] = hearingOfPartiesForm[key]
        }
        if (processingKeys.has(key)) {
          processing[key] = hearingOfPartiesForm[key]
        }
      }
      if (Object.keys(reception).length) {
        out.push({
          title: baseTitle + " / 1. Modtagelse",
          data: reception
        })
      }
      if (Object.keys(processing).length) {
        out.push({
          title: baseTitle + " / 2. Behandling",
          data: processing
        })
      }
      break;
    }

    case "resumption-conclusion":
    case "price_check-conclusion": {
      const hearingOfPartiesForm = data as CaseUpdatedChanges<ConclusionForm>;
      const receptionKeys = new Set<keyof ConclusionForm>(priceCheckNames.conclusion.reception);
      const reception: Record<string, unknown> = {}

      for (const k in hearingOfPartiesForm) {
        const key = k as keyof ConclusionForm
        if (receptionKeys.has(key)) {
          reception[key] = hearingOfPartiesForm[key]
        }
      }
      if (Object.keys(reception).length) {
        out.push({
          title: baseTitle + " / 1. Modtagelse",
          data: reception
        })
      }
      break;
    }

    case "resumption-request": {
      const requestForm = data as CaseUpdatedChanges<RequestForm>;
      const submissionKeys = new Set<keyof RequestForm>(resumptionNames.request.submission);
      const receptionKeys = new Set<keyof RequestForm>(resumptionNames.request.reception);
      const submission: Record<string, unknown> = {}
      const reception: Record<string, unknown> = {}

      for (const k in requestForm) {
        const key = k as keyof RequestForm
        if (receptionKeys.has(key)) {
          reception[key] = requestForm[key]
        }
        if (submissionKeys.has(key)) {
          submission[key] = requestForm[key]
        }
      }
      if (Object.keys(submission).length) {
        out.push({
          title: baseTitle + " / 1. Indsendelse",
          data: submission
        })
      }
      if (Object.keys(reception).length) {
        out.push({
          title: baseTitle + " / 2. Modtagelse",
          data: reception
        })
      }
      break;
    }

    case "complaint-submission": {
      const submissionForm = data as CaseUpdatedChanges<SubmissionForm>;
      const submissionKeys = new Set<keyof SubmissionForm>(complaintNames.submission.submission);
      const submission: Record<string, unknown> = {}

      for (const k in submissionForm) {
        const key = k as keyof SubmissionForm
        if (submissionKeys.has(key)) {
          submission[key] = submissionForm[key]
        }
      }
      if (Object.keys(submission).length) {
        out.push({
          title: baseTitle + " / 1. Indsendelse",
          data: submission
        })
      }
      break;
    }
    case "complaint-casePresentation": {
      const presentationForm = data as CaseUpdatedChanges<CasePresentationForm>;
      const submissionKeys = new Set<keyof CasePresentationForm>(complaintNames.casePresentation.presentation);
      const statementKeys = new Set<keyof CasePresentationForm>(complaintNames.casePresentation.statement);
      const presentation: Record<string, unknown> = {}
      const statement: Record<string, unknown> = {}

      for (const k in presentationForm) {
        const key = k as keyof CasePresentationForm
        if (submissionKeys.has(key)) {
          presentation[key] = presentationForm[key]
        }
        if (statementKeys.has(key)) {
          statement[key] = presentationForm[key]
        }
      }
      if (Object.keys(presentation).length) {
        out.push({
          title: baseTitle + " / 1. Sagsfremstilling",
          data: presentation
        })
      }
      if (Object.keys(statement).length) {
        out.push({
          title: baseTitle + " / 2. Udtalelse",
          data: statement
        })
      }
      break;
    }
    case "complaint-complaintConclusion": {
      const presentationForm = data as CaseUpdatedChanges<ComplaintConclusionForm>;
      const conclusionKeys = new Set<keyof ComplaintConclusionForm>(complaintNames.conclusion.conclusion);
      const conclusion: Record<string, unknown> = {}

      for (const k in presentationForm) {
        const key = k as keyof ComplaintConclusionForm
        if (conclusionKeys.has(key)) {
          conclusion[key] = presentationForm[key]
        }
      }
      if (Object.keys(conclusion).length) {
        out.push({
          title: baseTitle + " / 1. Afgørelse",
          data: conclusion
        })
      }
      break;
    }
    default: {
      out.push({
        title: baseTitle,
        data
      })
    }
  }

  return out
}


function StagesChanges({ stages }: { stages: CaseUpdatedChanges<TaxLegalStage[]> }) {

  const changes = useMemo(() => {
    const out: { title: string; data: StageFormUpdate; }[] = [];
    for (const stage of stages) {
      for (const formKey of Object.keys(stage?.data ?? {}) as StageFormNames[]) {
        const type = stage?.type?.new;
        const data = stage?.data?.[formKey];
        if (!type || !data) continue;
        if (stage?.type?.new && !!data) {
          const changes = handleFormUpdate(`${stage?.type?.new}-${formKey}`, data as StageFormUpdate)
          out.push(...changes ?? [])
        }
      }
    }
    return out
  }, [stages])

  return changes.length ? (
    <div className="mt-1">
      <h5 className="mb-03">Stadier</h5>
      {changes.map((stage, i) => {
        return (
          <div key={i} className={`${i > 0 ? "mt-05" : ""} pl-05 pr-05 pt-03 pb-03 border-light border-radius bg-white`}>
            <h5>{stage.title}</h5>
            <FieldChangeList changedFields={stage.data as ValueChange} />
          </div>
        )
      })}

    </div>

  ) : null
}
